/**
 * What every call to a service goes through.
 *
 * - `Authorization: Bearer`, taken from the session at the moment of sending.
 * - `Accept-Language`: the interface language, so `title` and field messages
 *   of a problem come back readable.
 * - `X-Request-ID`: one id per request.
 * - A token about to expire is refreshed first. A `401` that says the token
 *   is no good (`unauthenticated`, `invalid_token`) is refreshed and the
 *   request sent once more; `session_revoked` ends the session.
 * - Every failure leaves as a `ProblemError`. Calls never return an `error`
 *   to look at: they throw.
 */
import i18n from "i18next";
import createClient, { type Client, type Middleware } from "openapi-fetch";

import { refreshSession } from "@/shared/auth/refresh";
import { accessExpiresIn, endSession, getAccessToken, hasSession } from "@/shared/auth/session";
import { DEFAULT_LANGUAGE } from "@/shared/i18n/languages";
import { uuid } from "@/shared/lib/uuid";

import {
  CLIENT_CODES,
  isProblem,
  networkProblem,
  problemFromResponse,
  ProblemError,
} from "./problem";
import { serviceUrl, type Service } from "./services";

/** Refresh this long before the access token runs out, not after. */
const EXPIRY_MARGIN_MS = 10_000;

/** 401 codes that mean "this access token is no good; a new one may be". */
const REFRESHABLE = new Set(["unauthenticated", "invalid_token"]);

export async function authorizedFetch(request: Request): Promise<Response> {
  if (hasSession() && accessExpiresIn() < EXPIRY_MARGIN_MS) await refreshSession();

  const again = request.clone();
  const sentWith = getAccessToken();
  const response = await send(request);
  if (response.status !== 401 || !hasSession()) return response;

  const problem = await problemFromResponse(response);
  if (problem.code === "session_revoked") {
    endSession(problem.text("reason") ?? problem.code);
    return response;
  }
  if (!REFRESHABLE.has(problem.code)) return response;

  // Another request may have refreshed while this one was in the air.
  if (getAccessToken() === sentWith) await refreshSession();
  return send(again);
}

function send(request: Request): Promise<Response> {
  const token = getAccessToken();
  if (token !== null) request.headers.set("Authorization", `Bearer ${token}`);
  return fetch(request);
}

const common: Middleware = {
  onRequest({ request }) {
    request.headers.set("Accept-Language", i18n.resolvedLanguage ?? DEFAULT_LANGUAGE);
    if (!request.headers.has("X-Request-ID")) request.headers.set("X-Request-ID", uuid());
  },
  async onResponse({ response }) {
    if (!response.ok) throw await problemFromResponse(response);
  },
  onError({ error }) {
    // A refresh that failed already is a problem; an aborted request is the
    // caller's own doing and keeps its AbortError.
    if (isProblem(error)) return;
    if (error instanceof DOMException && error.name === "AbortError") return;
    return networkProblem(error);
  },
};

export function createServiceClient<Paths extends object>(
  service: Service,
  { bearer }: { bearer: boolean },
): Client<Paths> {
  const client = createClient<Paths>({
    baseUrl: serviceUrl(service),
    fetch: bearer ? authorizedFetch : (request) => fetch(request),
  });
  client.use(common);
  return client;
}

/**
 * The body of a call that answers with one. The middleware has thrown for
 * every failure by now, so a missing body means the service answered `2xx`
 * with nothing where the contract promises something.
 */
export async function unwrap<T>(call: Promise<{ data?: T; response: Response }>): Promise<T> {
  const { data, response } = await call;
  if (data === undefined) {
    throw new ProblemError({
      status: response.status,
      code: CLIENT_CODES.unexpected,
      title: "Empty response",
    });
  }
  return data;
}
